import { useState } from "react";
import { Mail, Loader2, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { emailSummary } from "../lib/api";
import { useT } from "../lib/i18n";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Lets a visitor email themselves a copy of a result and optionally opt in to health tips.
// variant "answer" renders a full card; "tool" renders a compact expandable button.
export function EmailSummaryForm({ title, summary, source, variant = "tool" }) {
  const t = useT();
  const [open, setOpen] = useState(variant === "answer");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const valid = EMAIL_RE.test(email.trim());

  const send = async () => {
    if (!valid || sending) return;
    setSending(true);
    try {
      const res = await emailSummary({ email: email.trim(), title, summary, source, consent });
      if (res && res.sent) {
        setSent(true);
        toast.success(t("email_sent"));
      } else {
        toast.error(t("email_fail"));
      }
    } catch {
      toast.error(t("email_fail"));
    } finally {
      setSending(false);
    }
  };

  const Form = (
    <div className="space-y-2.5">
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("email_ph")}
          data-testid="email-input"
          className="flex-1 bg-white border-2 border-slate-200 focus:border-teal-400 rounded-xl px-4 py-2.5 text-slate-900 outline-none transition-colors"
        />
        <button
          onClick={send}
          disabled={!valid || sending}
          data-testid="email-send"
          className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl transition-transform active:scale-95"
        >
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          {sending ? t("email_sending") : t("email_send")}
        </button>
      </div>
      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="email-consent"
          className="mt-1 w-4 h-4 accent-teal-600"
        />
        <span className="text-sm text-slate-600">{t("email_consent")}</span>
      </label>
    </div>
  );

  if (variant === "answer") {
    return (
      <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 sm:p-6" data-testid="email-summary" data-no-print>
        <h3 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2.5 mb-3">
          <Mail className="w-6 h-6 text-teal-600" /> {t("email_answer_title")}
        </h3>
        {sent ? (
          <p className="text-lg text-teal-700 font-semibold flex items-center gap-2" data-testid="email-sent">
            <Check className="w-5 h-5" /> {t("email_sent")}
          </p>
        ) : (
          <>
            <p className="text-base text-slate-600 mb-3">{t("email_desc")}</p>
            {Form}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid="email-summary" data-no-print>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="email-open"
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg border-2 border-slate-300 text-slate-700 hover:bg-white/60 transition-colors"
        >
          <Mail className="w-4 h-4" /> {t("email_it")}
        </button>
      ) : sent ? (
        <p className="text-sm text-teal-700 font-semibold flex items-center gap-1.5" data-testid="email-sent">
          <Check className="w-4 h-4" /> {t("email_sent")}
        </p>
      ) : (
        Form
      )}
    </div>
  );
}
